import React, { useEffect, useState, Suspense, useMemo } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import Loading from '../components/Loading';
import ModalManager from '../components/common/ModalManager';
import { useDispatch, useSelector } from 'react-redux';
import { setUser } from '../store/coreSlice';
import { traceLogout } from '../utils/logoutTrace';
import Login from '../screens/Login';
import AuthLayout from '../layouts/AuthLayout';
import PostLoginChecklist from '../screens/PostLoginChecklist';
import MobileAppDownload from '../screens/MobileAppDownload';
import { shouldPromptMobileAppDownload, isAddonBookingRoute } from '../utils/deviceInfo';

const AuraStructures = React.lazy(() => import('../screens/AuraStructures'));
const Ecosystem = React.lazy(() => import('../screens/admin/Ecosystem'));
const TipsWidget = React.lazy(() => import('../tips/TipsWidget'));
const Register = React.lazy(() => import('../screens/Register'));
const ModuleRender = React.lazy(() => import('../screens/ModuleRender'));
const LayoutSelector = React.lazy(() => import('../layouts/LayoutSelector'));
const ElementDetails = React.lazy(() => import('../screens/ElementDetails'));
const Billing = React.lazy(() => import('../screens/Billing'));
const AuraMessages = React.lazy(() => import('../screens/AuraMessages'));
const AuraAnalytics = React.lazy(() => import('../screens/AuraAnalytics'));
const AuraManage = React.lazy(() => import('../screens/AuraManage'));
const Dashboard = React.lazy(() => import('../components/dashboard/Dashboard'));
const AutomationsList = React.lazy(() => import('../screens/AutomationsList'));
const MyWork = React.lazy(() => import('../screens/MyWork'));
const BlankLayout = React.lazy(() => import('../screens/BlankLayout'));
const AuthCallback = React.lazy(() => import('../screens/AuthCallback'));
const Welcome = React.lazy(() => import('../screens/Welcome'));
const AuraAccounts = React.lazy(() => import('../screens/AuraAccounts'));
const AuraPublicPage = React.lazy(() => import('../screens/aura/AuraPublicPage'));
const AuraElementPage = React.lazy(() => import('../screens/aura/AuraElementPage'));
const AuraRetrievePage = React.lazy(() => import('../screens/aura/AuraRetrievePage'));
const Redirect = React.lazy(() => import('../screens/aura/Redirect'));
const Docs = React.lazy(() => import('../screens/Docs'));
const DashboardPulse = React.lazy(() => import('../screens/DashboardPulse'));
const Marketplace = React.lazy(() => import('../screens/Marketplace'));
const MaestroAccounts = React.lazy(() => import('../screens/maestro/MaestroAccounts'));
const MaestroConfig = React.lazy(() => import('../screens/maestro/MaestroConfig'));
const MaestroBilling = React.lazy(() => import('../screens/maestro/MaestroBilling'));
const MaestroOrders = React.lazy(() => import('../screens/maestro/MaestroOrders'));
const MaestroOrderDetails = React.lazy(() => import('../screens/maestro/MaestroOrderDetails'));
const AccountDetails = React.lazy(() => import('../screens/maestro/MaestroAccountDetails'));
const MaestroModels = React.lazy(() => import('../screens/maestro/MaestroModels'));
const MaestroModelDetails = React.lazy(() => import('../screens/maestro/MaestroModelDetails'));
const MaestroDocumentation = React.lazy(() => import('../screens/maestro/MaestroDocumentation'));
const MaestroAddonsStudio = React.lazy(() => import('../screens/maestro/MaestroAddonsStudio'));
const AddonBuilder = React.lazy(() => import('../screens/maestro/AddonBuilder'));
const Build = React.lazy(() => import('../screens/maestro/Build'));
const BookingAddonPage = React.lazy(() => import('../components/addons/booking/BookingPage'));
const BookingAddonManagePage = React.lazy(() => import('../components/addons/booking/BookingManagementEntry'));
const BookingManage = React.lazy(() => import('../screens/BookingManage'));
const OAuthCallbackHandler = React.lazy(() => import('../components/marketplace/OAuthCallbackHandler'));
const ValidateEmail = React.lazy(() => import('../screens/ValidateEmail'));
const OnboardingPage = React.lazy(() => import('../screens/onboarding/OnboardingPage'));
const OnboardingPreviewBootstrap = React.lazy(() => import('../screens/onboarding/OnboardingPreviewBootstrap'));
const CreateOnboardingPage = React.lazy(() => import('../screens/onboarding/CreateOnboardingPage'));
const BookOnboardingPage = React.lazy(() => import('../screens/onboarding/BookOnboardingPage'));
const PortalInvite = React.lazy(() => import('../screens/onboarding/PortalInvite'));
const DemoPage = React.lazy(() => import('../screens/admin/DemoPage'));
const LeadMagnetA = React.lazy(() => import('../screens/admin/LeadMagnetA'));
const Presentation = React.lazy(() => import('../screens/admin/Presentation'));
const PublicApprovalPage = React.lazy(() => import('../screens/PublicApprovalPage'));
const AvatarMobileUploadPage = React.lazy(() => import('../screens/AvatarMobileUploadPage'));
const FeedbackSurveyPage = React.lazy(() => import('../screens/FeedbackSurveyPage'));
const BusinessPublicPage = React.lazy(() => import('../screens/BusinessPublicPage'));
const DeskSequences = React.lazy(() => import('../screens/desk/DeskSequences'));
const DeskTemplates = React.lazy(() => import('../screens/desk/DeskTemplates'));
const DeskSuppressions = React.lazy(() => import('../screens/desk/DeskSuppressions'));
const AccountSettings = React.lazy(() => import('../screens/AccountSettings'));
const WelcomeInvite = React.lazy(() => import('../screens/WelcomeInvite'));
const DocsLayout = React.lazy(() => import('../components/docs/DocsLayout'));
const DocsBos = React.lazy(() => import('../pages/docs/DocsBos'));
const DocsConnectorMake = React.lazy(() => import('../pages/docs/DocsConnectorMake'));
const DocsConnectorZapier = React.lazy(() => import('../pages/docs/DocsConnectorZapier'));
const DocsWebhooks = React.lazy(() => import('../pages/docs/DocsWebhooks'));
const DocsConnectorsV2 = React.lazy(() => import('../pages/docs/DocsConnectorsV2'));
const LearningWorkshop = React.lazy(() => import('../screens/admin/LearningWorkshop'));
const AtelierList = React.lazy(() => import('../screens/admin/atelier/AtelierList'));
const AtelierDetail = React.lazy(() => import('../screens/admin/atelier/AtelierDetail'));
const AddonPageHost = React.lazy(() => import('../components/addons/AddonPageHost'));
const ExtensionPage = React.lazy(() => import('../components/uiExtensions/ExtensionSurfaces').then(m => ({ default: m.ExtensionPage })));

const PublicFormPage = React.lazy(() => import('../publicForms/PublicFormPage'));
const PublicFormChallengePage = React.lazy(() => import('../publicForms/Challenge').then(module => ({ default: module.ChallengePage })));

const McpAuthorize = React.lazy(() => import('../screens/McpAuthorize'));
const PROTECTED_PREFIXES = ['/bos', '/admin', '/pulse', '/desk', '/maestro', '/atelier', '/mcp', '/checklist'];

const RedirectToLogin = () => {
  const location = useLocation();
  const intended = location.pathname + location.search;
  if (PROTECTED_PREFIXES.some((p) => intended.startsWith(p))) {
    sessionStorage.setItem('intendedUrl', intended);
    sessionStorage.setItem('freshProtectedLoginRedirect', 'true');
  }
  return <Navigate to="/" replace />;
};

const RestoreIntendedUrl = () => {
  const navigate = useNavigate();
  useEffect(() => {
    const intended = sessionStorage.getItem('intendedUrl');
    if (intended) {
      sessionStorage.removeItem('intendedUrl');
      navigate(intended, { replace: true });
    }
  }, [navigate]);
  return null;
};

const AuthenticatedIndex = () => {
  if (sessionStorage.getItem('showPostLoginChecklist') === 'true') {
    return null;
  }

  return (
    <Suspense fallback={<Loading fullscreen />}>
      <Dashboard />
    </Suspense>
  );
};

const AppRouter = () => {
  const dispatch = useDispatch();
  const location = useLocation();

  const user = useSelector((state) => state.core.user);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      const isPreviewRoute = window.location.pathname.includes('/onboarding/')
        && window.location.pathname.includes('/preview');
      const isInsideIframe = window.self !== window.top;

      if (isPreviewRoute || isInsideIframe) {
        setLoading(false);
        return;
      }

      // Clear stale preview session if we're not on a preview route
      if (sessionStorage.getItem('onboardingPreviewActive')) {
        sessionStorage.removeItem('onboardingPreviewActive');
      }

      if (user) {
        if (user.uid?.startsWith('preview-')) {
          const isOnboardingRoute = window.location.pathname.startsWith('/onboarding/');
          if (isOnboardingRoute) {
            setLoading(false);
            return;
          }
          traceLogout('preview-session-outside-onboarding');
          await auth.signOut();
          return;
        }

        const { uid, email, displayName, photoURL } = user;
        dispatch(setUser({ uid, email, displayName, photoURL }));
      } else {
        dispatch(setUser(null));
      }
      setLoading(false);
    });

    return unsubscribe;
  }, [dispatch]);

  const path = window.location.pathname;
  const displayTips = useMemo(() => {
    return path.includes('/services/') || path.includes('/articles/');
  }, [user, path]);

  if (location.pathname === '/security' || location.pathname === '/security/') {
    window.location.replace('https://usenode.com/security');
    return null;
  }

  if (loading) {
    return <Loading />;
  }

  if (shouldPromptMobileAppDownload(location.pathname)) {
    return <MobileAppDownload />;
  }

  const isInvitationRoute = location.pathname.startsWith('/welcome/')
    || location.pathname.startsWith('/admin/portal/invitation/');

  return (
    <>
      <ModalManager />
      {user?.uid && !isInvitationRoute && !isAddonBookingRoute(location.pathname) && <RestoreIntendedUrl />}
      <Suspense fallback={null}>
        {displayTips && <TipsWidget />}
      </Suspense>
      <Routes>
        <Route path="/forms/challenge" element={<Suspense fallback={<Loading fullscreen />}><PublicFormChallengePage /></Suspense>} />
        <Route path="/forms/:formId" element={<Suspense fallback={<Loading fullscreen />}><PublicFormPage /></Suspense>} />
        <Route path="/auth-callback" element={
          <Suspense fallback={<Loading fullscreen />}>
            <AuthCallback />
          </Suspense>
        } />
        <Route path="/validate" element={
          <Suspense fallback={<Loading fullscreen />}>
            <ValidateEmail />
          </Suspense>
        } />
        <Route
          path="/oauth/callback/:provider"
          element={
            <Suspense fallback={<Loading fullscreen />}>
              <OAuthCallbackHandler />
            </Suspense>
          }
        />
        <Route
          path="/aura/board/:boardId/:elementId"
          element={
            <Suspense fallback={<Loading fullscreen />}>
              <AuraPublicPage />
            </Suspense>
          }
        />

        <Route
          path="/redirect/:businessId/:collection/:structureId/:elementId"
          element={
            <Suspense fallback={<Loading fullscreen />}>
              <Redirect />
            </Suspense>
          }
        />
        <Route path="/booking-addon/manage" element={<Suspense fallback={<Loading fullscreen />}><BookingAddonManagePage /></Suspense>} />
        <Route path="/booking-addon/:businessId/embed" element={<Suspense fallback={<Loading fullscreen />}><BookingAddonPage /></Suspense>} />
        <Route path="/booking-addon/:businessId" element={<Suspense fallback={<Loading fullscreen />}><BookingAddonPage /></Suspense>} />
        <Route path="/booking/manage" element={
          <Suspense fallback={<Loading fullscreen />}>
            <BookingManage />
          </Suspense>
        } />
        <Route path="/build" element={
          <Suspense fallback={<Loading fullscreen />}>
            <Build />
          </Suspense>
        } />

        <Route path="/presentation" element={
          <Suspense fallback={<Loading fullscreen />}>
            <Presentation />
          </Suspense>
        } />
        <Route path="/demo" element={
          <Suspense fallback={<Loading fullscreen />}>
            <DemoPage />
          </Suspense>
        } />
        <Route path="/demo/:onboardingId" element={
          <Suspense fallback={<Loading fullscreen />}>
            <DemoPage />
          </Suspense>
        } />
        <Route path="/diagnostic-ops" element={
          <Suspense fallback={<Loading fullscreen />}>
            <LeadMagnetA />
          </Suspense>
        } />
        <Route path="/ecosystem" element={
          <Suspense fallback={<Loading fullscreen />}>
            <Ecosystem />
          </Suspense>
        } />

        {/* Onboarding - Public routes */}
        <Route path="/onboarding/new" element={
          <Suspense fallback={<Loading fullscreen />}>
            <CreateOnboardingPage />
          </Suspense>
        } />
        <Route path="/onboarding/book" element={
          <Suspense fallback={<Loading fullscreen />}>
            <BookOnboardingPage />
          </Suspense>
        } />
        <Route path="/onboarding/:onboardingId/preview" element={
          <Suspense fallback={<Loading fullscreen />}>
            <OnboardingPreviewBootstrap />
          </Suspense>
        } />
        <Route path="/onboarding/:onboardingId" element={
          <Suspense fallback={<Loading fullscreen />}>
            <OnboardingPage />
          </Suspense>
        } />
        <Route path="/welcome/:businessId" element={
          <Suspense fallback={<Loading fullscreen />}>
            <AuthLayout />
          </Suspense>
        }>
          <Route index element={
            <Suspense fallback={<Loading fullscreen />}>
              <WelcomeInvite />
            </Suspense>
          } />
        </Route>
        {/* Invitation au portail administratif (role BILLING) — publique:
            l'invite n'a pas encore de compte Node, /admin est protege. */}
        <Route path="/admin/portal/invitation/:businessId" element={
          <Suspense fallback={<Loading fullscreen />}>
            <PortalInvite />
          </Suspense>
        } />

        {/* Public Approval Page */}
        <Route path="/public-approval" element={
          <Suspense fallback={<Loading fullscreen />}>
            <PublicApprovalPage />
          </Suspense>
        } />

        {/* Avatar QR "continue on mobile" upload page */}
        <Route path="/avatar-upload" element={
          <Suspense fallback={<Loading fullscreen />}>
            <AvatarMobileUploadPage />
          </Suspense>
        } />

        <Route path="/feedback/:businessId/:feedbackRequestId" element={
          <Suspense fallback={<Loading fullscreen />}>
            <FeedbackSurveyPage />
          </Suspense>
        } />

        <Route
          path="/doc/:businessId/:collection/:structureId/:elementId"
          element={
            <Suspense fallback={<Loading fullscreen />}>
              <AuraElementPage />
            </Suspense>
          }
        />
        <Route
          path="/find/:businessId/:structureId"
          element={
            <Suspense fallback={<Loading fullscreen />}>
              <AuraRetrievePage />
            </Suspense>
          }
        />
        <Route
          path="/page/:slug"
          element={
            <Suspense fallback={<Loading fullscreen />}>
              <BusinessPublicPage />
            </Suspense>
          }
        />

        {/* Public API documentation */}
        <Route path="/docs" element={
          <Suspense fallback={<Loading fullscreen />}>
            <DocsLayout />
          </Suspense>
        }>
          <Route path="bos" element={<Suspense fallback={<Loading fullscreen />}><DocsBos /></Suspense>} />
          <Route path="connectors" element={<Suspense fallback={<Loading fullscreen />}><DocsConnectorsV2 /></Suspense>} />
          <Route path="make" element={<Suspense fallback={<Loading fullscreen />}><DocsConnectorMake /></Suspense>} />
          <Route path="zapier" element={<Suspense fallback={<Loading fullscreen />}><DocsConnectorZapier /></Suspense>} />
          <Route path="webhooks" element={<Suspense fallback={<Loading fullscreen />}><DocsWebhooks /></Suspense>} />
        </Route>

        {user?.uid ? (
          <>
            <Route path="/mcp/authorize" element={<Suspense fallback={<Loading fullscreen />}><McpAuthorize /></Suspense>} />
            <Route path="/mcp/connections" element={<Suspense fallback={<Loading fullscreen />}><McpAuthorize /></Suspense>} />
            <Route path="/admin/apprentissage" element={<Suspense fallback={<Loading fullscreen />}><LearningWorkshop /></Suspense>} />
            <Route path="/atelier" element={
              <Suspense fallback={<Loading fullscreen />}>
                <AtelierList />
              </Suspense>
            } />
            <Route path="/atelier/:onboardingId" element={
              <Suspense fallback={<Loading fullscreen />}>
                <AtelierDetail />
              </Suspense>
            } />
            <Route path="/admin/portal" element={
              <Suspense fallback={<Loading fullscreen />}>
                <OnboardingPage />
              </Suspense>
            } />
            <Route path="/checklist" element={
                <AuthLayout />
            }>
              <Route index element={
                  <PostLoginChecklist />
              } />
            </Route>
            <Route path="/" element={
              <Suspense fallback={<Loading fullscreen />}>
                <LayoutSelector />
              </Suspense>
            }>
              <Route index element={<AuthenticatedIndex />} />
              <Route path="bos/extension/:extensionId" element={<Suspense fallback={<Loading fullscreen />}><ExtensionPage /></Suspense>} />
              <Route
                path="bos/addon/:addonId/:routeKey"
                element={
                  <Suspense fallback={<Loading fullscreen />}>
                    <AddonPageHost />
                  </Suspense>
                }
              />
              <Route
                path="/bos/:collection/:moduleName/:moduleId/:structureId"
                element={
                  <Suspense fallback={<Loading fullscreen />}>
                    <ModuleRender />
                  </Suspense>
                }
              />
              <Route path="/docs" element={
                <Suspense fallback={<Loading fullscreen />}>
                  <Docs />
                </Suspense>
              } />
              <Route
                path="bos/element/:moduleName/:structureId/:elementId"
                element={
                  <Suspense fallback={<Loading fullscreen />}>
                    <ElementDetails />
                  </Suspense>
                }
              />
              <Route path="admin/automations" element={
                <Suspense fallback={<Loading fullscreen />}>
                  <AutomationsList />
                </Suspense>
              } />

              <Route path="admin/my-work" element={
                <Suspense fallback={<Loading fullscreen />}>
                  <MyWork />
                </Suspense>
              } />
              <Route path="admin/billing" element={
                <Suspense fallback={<Loading fullscreen />}>
                  <Billing />
                </Suspense>
              } />
              <Route path="admin/account" element={
                <Suspense fallback={<Loading fullscreen />}>
                  <AccountSettings />
                </Suspense>
              } />
              <Route path="/aura/structures/:structureId" element={
                <Suspense fallback={<Loading fullscreen />}>
                  <AuraStructures />
                </Suspense>
              } />
              <Route path="aura/messages" element={
                <Suspense fallback={<Loading fullscreen />}>
                  <AuraMessages />
                </Suspense>
              } />
              <Route path="aura/analytics" element={
                <Suspense fallback={<Loading fullscreen />}>
                  <AuraAnalytics />
                </Suspense>
              } />
              <Route path="aura/accounts" element={
                <Suspense fallback={<Loading fullscreen />}>
                  <AuraAccounts />
                </Suspense>
              } />
              <Route path="aura" element={
                <Suspense fallback={<Loading fullscreen />}>
                  <AuraManage />
                </Suspense>
              } />
              <Route path="marketplace" element={
                <Suspense fallback={<Loading fullscreen />}>
                  <Marketplace />
                </Suspense>
              } />
              <Route path="pulse" element={
                <Suspense fallback={<Loading fullscreen />}>
                  <BlankLayout />
                </Suspense>
              } />
              <Route path="maestro" element={
                <Suspense fallback={<Loading fullscreen />}>
                  <BlankLayout />
                </Suspense>
              } />
              <Route path="maestro/accounts" element={
                <Suspense fallback={<Loading fullscreen />}>
                  <MaestroAccounts />
                </Suspense>
              } />
              <Route path="maestro/models" element={
                <Suspense fallback={<Loading fullscreen />}>
                  <MaestroModels />
                </Suspense>
              } />
              <Route path="maestro/documentation" element={
                <Suspense fallback={<Loading fullscreen />}>
                  <MaestroDocumentation />
                </Suspense>
              } />
              <Route path="maestro/addons-studio" element={
                <Suspense fallback={<Loading fullscreen />}>
                  <MaestroAddonsStudio />
                </Suspense>
              } />
              <Route path="maestro/addons-studio/builder/:draftId" element={
                <Suspense fallback={<Loading fullscreen />}>
                  <AddonBuilder />
                </Suspense>
              } />
              <Route
                path="maestro/models/:modelId"
                element={
                  <Suspense fallback={<Loading fullscreen />}>
                    <MaestroModelDetails />
                  </Suspense>
                }
              />
              <Route
                path="maestro/accounts/:accountId"
                element={
                  <Suspense fallback={<Loading fullscreen />}>
                    <AccountDetails />
                  </Suspense>
                }
              />
              <Route
                path="maestro/configurations"
                element={
                  <Suspense fallback={<Loading fullscreen />}>
                    <MaestroConfig />
                  </Suspense>
                }
              />
              <Route path="maestro/billing" element={
                <Suspense fallback={<Loading fullscreen />}>
                  <MaestroBilling />
                </Suspense>
              } />
              <Route path="maestro/orders" element={
                <Suspense fallback={<Loading fullscreen />}>
                  <MaestroOrders />
                </Suspense>
              } />
              <Route
                path="maestro/orders/:orderId"
                element={
                  <Suspense fallback={<Loading fullscreen />}>
                    <MaestroOrderDetails />
                  </Suspense>
                }
              />

              <Route path="pulse/:pulseId" element={
                <Suspense fallback={<Loading fullscreen />}>
                  <DashboardPulse />
                </Suspense>
              } />
              
              <Route path="bos" element={
                <Suspense fallback={<Loading fullscreen />}>
                  <BlankLayout />
                </Suspense>
              } />

              {/* DESK Routes */}
              <Route path="desk" element={
                <Suspense fallback={<Loading fullscreen />}>
                  <BlankLayout />
                </Suspense>
              } />
              <Route path="desk/sequences" element={
                <Suspense fallback={<Loading fullscreen />}>
                  <DeskSequences />
                </Suspense>
              } />
              <Route path="desk/templates" element={
                <Suspense fallback={<Loading fullscreen />}>
                  <DeskTemplates />
                </Suspense>
              } />
              <Route path="desk/suppressions" element={
                <Suspense fallback={<Loading fullscreen />}>
                  <DeskSuppressions />
                </Suspense>
              } />
              <Route path="desk/docs" element={
                <Suspense fallback={<Loading fullscreen />}>
                  <Docs />
                </Suspense>
              } />

              <Route path="welcome-osiris" element={
                <Suspense fallback={<Loading fullscreen />}>
                  <Welcome />
                </Suspense>
              } />
            </Route>
          </>
        ) : (
          <>
            <Route path="/" element={
                <AuthLayout />
            }>
              <Route index element={
                  <Login />
              } />
              <Route path="/register" element={
                <Suspense fallback={<Loading fullscreen />}>
                  <Register />
                </Suspense>
              } />
            </Route>
            <Route path="*" element={<RedirectToLogin />} />
          </>
        )}
      </Routes>
    </>
  );
};

export default AppRouter;
